import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import i18n from 'i18next';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { ALL, statusCampaign } from '@/helpers/constants';
import { isSubCRById } from '@/helpers/utils';
import useAuth from '@/hooks/useAuth';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { setSettingsInfo } from '@/redux/User/user.action';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import notificationsService from '@/services/notificationsService';
import usersServices from '@/services/usersServices';
import { Notification as NotificationType, User, UserSettings } from '@/types';
import NotificationsButton from './NotificationsButton/NotificationsButton';
import NotificationsMenu from './NotificationsMenu/NotificationsMenu';

interface NotificationsProps {
  userSettings: User;
  setSettings: (settings: UserSettings) => void;
}

export interface StateComp {
  countInfo: { bellsCount: number; thereAreOldNotifications: boolean };
  recentNotifications: NotificationType[];
  allNotifications: NotificationType[];
  displayAll: boolean;
}

const Notifications = (props: NotificationsProps) => {
  const { userSettings, setSettings } = props;
  const { goTo } = useGoTo();
  const { addToast } = useToastContext();
  const { handleCriticalError } = useError();
  const { loading: globalLoading, setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const { t } = useTranslation();
  const authService = useAuth();
  const { roleSelected } = userSettings;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [removeNotifications, setRemoveNotifications] = useState<string | null>();
  const [state, setState] = useState<StateComp>({
    countInfo: { bellsCount: 0, thereAreOldNotifications: false },
    recentNotifications: [],
    allNotifications: [],
    displayAll: false,
  });
  const notificationsFromState = state.displayAll ? state.allNotifications : state.recentNotifications;

  useEffect(() => {
    if (globalLoading) return;

    notificationsService
      .getCounterNotifications()
      .then((countInfo) => setState({ ...state, countInfo }));
  }, [roleSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => setAnchorEl(null);

  const getEventNotification = async () => {
    if (authService.isLogged) {
      try {
        const countInfo = await notificationsService.getCounterNotifications();
        setState({ ...state, countInfo });
        handleClose();
      } catch (err: any) {
        addToast(err.message, 'Error');
      }
    }
  };

  useEffect(() => {
    const intervalNotifications = setInterval(() => {
      getEventNotification();
    }, 120000);

    return () => clearInterval(intervalNotifications);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getNotificationsFromBack = (all: boolean) => (
    all ? notificationsService.getAllNotifications() : notificationsService.getRecentNotifications());

  const getNumberOfUnreadNots = (notifications: NotificationType[]): number => notifications.filter(({ readByUser }) => !readByUser)?.length || 0;

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      setLoading(true);
      setAnchorEl(event.currentTarget);

      const notifications = await getNotificationsFromBack(false);
      setState({
        displayAll: false,
        recentNotifications: notifications,
        countInfo: {
          ...state.countInfo,
          bellsCount: getNumberOfUnreadNots(notifications),
        },
        allNotifications: [],
      });
    } catch (err: any) {
      addToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayAll = async () => {
    try {
      setLoading(true);
      const notifications = await getNotificationsFromBack(!state.displayAll);
      setState({
        ...state,
        [state.displayAll ? 'recentNotifications' : 'allNotifications']: notifications,
        displayAll: !state.displayAll,
        countInfo: {
          ...state.countInfo,
          bellsCount: getNumberOfUnreadNots(notifications),
        },
      });
    } catch (err: any) {
      addToast(err.message || 'Error loading notifications', 'ERROR');
    } finally {
      setLoading(false);
    }
  };

  const changeSendNotifications = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalLoading(true);
    usersServices
      .saveUsersSettings(authService.userCode, { ...userSettings, notificationAsEmail: ev.target.checked })
      .then(() => usersServices.getUsersSettings(authService.userCode).then(setSettings))
      .catch(handleCriticalError)
      .finally(() => setGlobalLoading(false));
  };

  const handlePush = (id: string, campaignId: string, code: string) => {
    const baseUrl = isSubCRById(campaignId) ? `/sub-campaign/${campaignId}` : `/campaign/${campaignId}`;
    notificationsService.updateBells(id)
      .then(() => {
        if (code === statusCampaign.IN_CREATION) {
          goTo(`${baseUrl}/description`);
        } else {
          goTo(`${baseUrl}/summary`);
        }
        handleClose();
      }).catch((error) => addToast(error.message, 'ERROR'));
  };

  const handleMarkAllRed = async () => {
    try {
      const res = await notificationsService.markAsReadAll();
      setState({
        ...state,
        countInfo: {
          ...state.countInfo,
          bellsCount: getNumberOfUnreadNots(res),
        },
        [state.displayAll ? 'allNotifications' : 'recentNotifications']: res,
      });
    } catch (error) {
      handleCriticalError(error);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleDeleteNotification = async () => {
    try {
      const isAll = removeNotifications === ALL;
      const onlyId = isAll ? undefined : removeNotifications;
      const result = isAll ? [] : (notificationsFromState.filter((n) => n.id !== removeNotifications));

      await notificationsService.removeBells(onlyId);
      setState({
        ...state,
        countInfo: {
          ...state.countInfo,
          bellsCount: getNumberOfUnreadNots(result),
        },
        [state.displayAll ? 'allNotifications' : 'recentNotifications']: result,
      });
      setRemoveNotifications(null);
    } catch (error) {
      handleCriticalError(error);
    } finally {
      setGlobalLoading(false);
    }
  };

  const getNotificationStringByRole = (code: string, userCode: string, campaignId: string) => {
    const stringLiteral = `notifications${isSubCRById(campaignId) ? '.subCampaign' : ''}.${code}`;
    const userRoles = authService.getUserRole(true);

    for (let i = 0; i < userRoles.length; i++) {
      if (i18n.exists(`${stringLiteral}__${userRoles[i]}`)) return t(`${stringLiteral}__${userRoles[i]}`, { x: userCode, wkz: campaignId.slice(-3) });
    }

    return t(`${stringLiteral}`, { x: userCode, wkz: campaignId.slice(-8, -5) });
  };

  return (
    <>
      <NotificationsButton
        id="notifications-button"
        data-testid="notifications-button"
        aria-controls="notifications-menu"
        badge={state.countInfo?.bellsCount}
        onClick={handleClick}

      />
      <NotificationsMenu
        notifications={notificationsFromState}
        displayAll={state.displayAll}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onDisplayAll={handleDisplayAll}
        onClose={handleClose}
        isLoading={loading}
        onPush={handlePush}
        onDeleteNotification={(id) => setRemoveNotifications(id)}
        state={state}
        onMarkAllRead={handleMarkAllRed}
        getNotificationStringByRole={getNotificationStringByRole}
        changeSendNotifications={changeSendNotifications}
        setState={setState}
        hasOldNotifications={state.countInfo?.thereAreOldNotifications}
      />
      {removeNotifications && (
        <ConfirmDialog
          close={() => setRemoveNotifications(null)}
          confirm={handleDeleteNotification}
          title={t('dialog.confirmDelete')}
          message={t('dialog.wantToDelete')}
          closeBtnText={t('dialog.no')}
          confirmBtnText={t('dialog.yes')}
        />
      )}
    </>

  );
};

const actionCreators = {
  setSettings: setSettingsInfo,
};

const mapStateToProps = (state) => ({
  userSettings: getSettingsInfo(state),
});

export default React.memo(connect(mapStateToProps, actionCreators)(Notifications));
